import React, { useState, useRef, useEffect } from 'react';
import { searchDoctors } from '../../services/npiService';
import { lbl, inp, row2, sectionDivider, sectionHeading, NoneCheckbox } from './shared';

const dropdownStyle = {
  position: 'absolute', top: '100%', left: 0, right: 0,
  background: '#fff', border: '1.5px solid #c5d5e8', borderRadius: 8,
  zIndex: 200, maxHeight: 220, overflowY: 'auto',
  boxShadow: '0 6px 16px rgba(26,51,100,0.13)', marginTop: 2,
};

const optionStyle = {
  padding: '9px 14px', cursor: 'pointer', fontSize: 14, color: '#1a3364', background: '#fff',
};

const PROVIDER_OPTIONS = [
  'Aetna', 'Anthem', 'Blue Cross Blue Shield', 'Bright Health',
  'CareFirst BlueCross BlueShield', 'Centene', 'Cigna',
  'CVS Health / Caremark', 'Florida Blue', 'Harvard Pilgrim Health Care',
  'Health Net', 'HealthPartners', 'Highmark', 'Humana',
  'Independence Blue Cross', 'Kaiser Permanente', 'Medica',
  'Molina Healthcare', 'Oscar Health', 'Premera Blue Cross',
  'Priority Health', 'Providence Health Plan',
  'Regence BlueCross BlueShield', 'Tufts Health Plan',
  'UPMC Health Plan', 'UnitedHealthcare', 'WellCare',
].sort();

function ProviderTypeahead({ value, onChange, placeholder }) {
  const [query, setQuery] = useState(value || '');
  const [open, setOpen] = useState(false);
  const containerRef = useRef(null);

  // Rows are keyed by index, so removing a row hands this input a different
  // row's value - without this it kept showing the removed row's text.
  useEffect(() => {
    setQuery(value || '');
  }, [value]);

  useEffect(() => {
    function handleClick(e) {
      if (containerRef.current && !containerRef.current.contains(e.target)) {
        setOpen(false);
      }
    }
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, []);

  const matches = query.length >= 1
    ? PROVIDER_OPTIONS.filter(p => p.toLowerCase().includes(query.toLowerCase()))
    : PROVIDER_OPTIONS;

  function select(name) {
    setQuery(name);
    onChange(name);
    setOpen(false);
  }

  return (
    <div ref={containerRef} style={{ position: 'relative' }}>
      <input
        style={inp}
        value={query}
        placeholder={placeholder}
        autoComplete="off"
        onChange={e => { setQuery(e.target.value); onChange(e.target.value); setOpen(true); }}
        onFocus={() => setOpen(true)}
      />
      {open && matches.length > 0 && (
        <div style={dropdownStyle}>
          {matches.map(p => (
            <div
              key={p}
              onMouseDown={() => select(p)}
              style={optionStyle}
              onMouseEnter={e => e.currentTarget.style.background = '#dbeef8'}
              onMouseLeave={e => e.currentTarget.style.background = '#fff'}
            >
              {p}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function PreferredProviders({ data, onChange, ignoreClicksUntilRef }) {
  function addRow() {
    // Picking a doctor collapses the results list and slides this button up
    // under the mouse, so a double-click (or quick second click) on a result
    // was landing here and adding empty rows.
    if (Date.now() < ignoreClicksUntilRef.current) return;
    onChange('preferredProviders', [...(data.preferredProviders || ['']), '']);
  }

  function updateProvider(i, val) {
    const arr = [...(data.preferredProviders || [''])];
    arr[i] = val;
    onChange('preferredProviders', arr);
  }

  return (
    <div style={sectionDivider}>
      <div style={sectionHeading}>Preferred Insurance Companies</div>
      <p style={{ fontSize: 13, color: '#4a6288', marginTop: 0, marginBottom: 14 }}>
        Insurance companies or health networks you prefer (e.g. UnitedHealthcare, Kaiser, Providence).
        This is <strong>not</strong> for doctors - add doctors with the search above.
      </p>
      {(data.preferredProviders || ['']).map((val, i) => (
        <div key={i} style={{ display: 'flex', gap: 8, marginBottom: 10, alignItems: 'center' }}>
          <div style={{ flex: 1 }}>
            <ProviderTypeahead
              value={val}
              onChange={v => updateProvider(i, v)}
              placeholder="Type to search or enter any name…"
            />
          </div>
          {(data.preferredProviders || ['']).length > 1 && (
            <button
              type="button"
              onClick={() => onChange('preferredProviders', (data.preferredProviders || ['']).filter((_, idx) => idx !== i))}
              style={{ padding: '9px 12px', border: 'none', background: '#f5e8e8', borderRadius: 8, cursor: 'pointer', color: '#b03030', fontWeight: 700, fontSize: 18, lineHeight: 1, flexShrink: 0 }}
            >
              ×
            </button>
          )}
        </div>
      ))}
      <button
        type="button"
        onClick={addRow}
        style={{ padding: '9px 20px', border: '2px dashed #2a5298', borderRadius: 8, background: '#f4f8fc', color: '#2a5298', fontWeight: 600, fontSize: 14, cursor: 'pointer', marginTop: 2 }}
      >
        + Add Insurance Company
      </button>
    </div>
  );
}

// Text typed into the search or manual-entry boxes is NOT saved - a doctor is
// only added by clicking a search result or "+ Add Doctor". Clients were
// typing a name and clicking Next (or "+ Add Provider"), losing the doctor
// with no feedback, so the wizard blocks Next with this message instead.
function describePendingInput({ firstName, lastName, manualMode, manualName, manualSpecialty, manualCity, manualState }) {
  const typedName = [firstName.trim(), lastName.trim()].filter(Boolean).join(' ');
  if (typedName) {
    return `You typed "${typedName}" in the doctor search but haven't added them yet. Click Search, then click your doctor in the results to add them. If you don't want to add this doctor, clear the name boxes.`;
  }
  if (manualMode && [manualName, manualSpecialty, manualCity, manualState].some(v => v.trim())) {
    const label = manualName.trim() ? `"${manualName.trim()}"` : 'a doctor';
    return `You started entering ${label} manually but haven't added them yet. Click "+ Add Doctor" to add them, or click Cancel to discard.`;
  }
  return '';
}

export default function Doctors({ data, onChange, onPendingInputChange }) {
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [outOfState, setOutOfState] = useState(false);
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searched, setSearched] = useState(false);
  const [searchError, setSearchError] = useState('');
  const [manualMode, setManualMode] = useState(false);
  const [manualName, setManualName] = useState('');
  const [manualSpecialty, setManualSpecialty] = useState('');
  const [manualCity, setManualCity] = useState('');
  const [manualState, setManualState] = useState('');
  const firstNameRef = useRef(null);
  // See PreferredProviders' addRow - swallow the stray second click that
  // follows picking a search result, since the layout shifts under it.
  const ignoreClicksUntilRef = useRef(0);

  const doctors = data.doctors || [];
  const clientState = data.state || '';

  const pendingInput = describePendingInput({ firstName, lastName, manualMode, manualName, manualSpecialty, manualCity, manualState });
  useEffect(() => {
    onPendingInputChange?.(pendingInput);
  }, [pendingInput, onPendingInputChange]);
  useEffect(() => () => onPendingInputChange?.(''), [onPendingInputChange]);

  async function handleSearch(e) {
    e.preventDefault();
    if (!lastName.trim()) return;
    setLoading(true);
    setSearched(false);
    setResults([]);
    setSearchError('');
    try {
      const state = outOfState ? '' : clientState;
      const res = await searchDoctors(firstName, lastName, state);
      setResults(res);
      setSearched(true);
    } catch (err) {
      console.error('[Doctor Search] Error:', err);
      setSearchError(`${err.message || 'Doctor search failed.'} Please try again, or check "Can't find your doctor?" below to enter them manually.`);
      setResults([]);
      setSearched(true);
    }
    setLoading(false);
  }

  function handleSelect(doctor) {
    ignoreClicksUntilRef.current = Date.now() + 800;
    onChange('doctors', [...doctors, doctor]);
    setFirstName('');
    setLastName('');
    setResults([]);
    setSearched(false);
    setTimeout(() => firstNameRef.current?.focus(), 50);
  }

  function handleRemove(i) {
    if (Date.now() < ignoreClicksUntilRef.current) return;
    onChange('doctors', doctors.filter((_, idx) => idx !== i));
  }

  function handleAddManual(e) {
    e.preventDefault();
    if (!manualName.trim()) return;
    onChange('doctors', [...doctors, {
      npi: '',
      name: manualName.trim(),
      credential: '',
      specialty: manualSpecialty.trim(),
      city: manualCity.trim(),
      state: manualState.trim(),
    }]);
    setManualName('');
    setManualSpecialty('');
    setManualCity('');
    setManualState('');
    setManualMode(false);
  }

  return (
    <div>
      <div style={{
        background: '#f4f8fc', border: '1.5px solid #dbeef8',
        borderRadius: 8, padding: '12px 16px', marginBottom: 16,
        fontSize: 13, color: '#4a6288', lineHeight: 1.6,
      }}>
        <strong style={{ color: '#1a3364' }}>How to add doctors:</strong>
        <ol style={{ margin: '6px 0 0', paddingLeft: 20 }}>
          <li>Enter a last name (required) and first name (optional), then click <strong>Search</strong>.</li>
          <li>Click the correct doctor from the results to add them to your list.</li>
          <li>Repeat for each provider.</li>
        </ol>
      </div>

      <div style={{
        background: '#f4f8fc', border: '1.5px solid #dbeef8',
        borderRadius: 10, padding: '18px 18px 14px', marginBottom: 20,
      }}>
        <form onSubmit={handleSearch}>
          <div style={row2}>
            <div>
              <label style={lbl}>First Name <span style={{ fontWeight: 400, textTransform: 'none', letterSpacing: 0 }}>(optional)</span></label>
              <input
                ref={firstNameRef}
                style={{ ...inp, background: '#fff' }}
                value={firstName}
                onChange={e => setFirstName(e.target.value)}
                placeholder="e.g. Jane"
                autoComplete="off"
              />
            </div>
            <div>
              <label style={lbl}>Last Name <span style={{ fontWeight: 400, textTransform: 'none', letterSpacing: 0 }}>(required)</span></label>
              <input
                style={{ ...inp, background: '#fff' }}
                value={lastName}
                onChange={e => setLastName(e.target.value)}
                placeholder="e.g. Smith"
                autoComplete="off"
              />
            </div>
          </div>

          {clientState && (
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 12 }}>
              <input
                type="checkbox"
                id="outOfState"
                checked={outOfState}
                onChange={e => setOutOfState(e.target.checked)}
                style={{ width: 16, height: 16, cursor: 'pointer', accentColor: '#1a3364' }}
              />
              <label htmlFor="outOfState" style={{ fontSize: 13, color: '#4a6288', cursor: 'pointer' }}>
                Search outside {clientState} (out-of-state specialists)
              </label>
            </div>
          )}

          <button
            type="submit"
            disabled={!lastName.trim() || loading}
            style={{
              padding: '9px 22px', border: 'none', borderRadius: 8,
              background: lastName.trim() && !loading ? '#2a5298' : '#b8cfe8',
              color: '#fff', fontWeight: 600, fontSize: 14,
              cursor: lastName.trim() && !loading ? 'pointer' : 'default',
              marginBottom: 12,
            }}
          >
            {loading ? 'Searching...' : 'Search'}
          </button>

          {searchError && (
            <div style={{ fontSize: 13, color: '#c0392b', background: '#fdf0ef', border: '1px solid #f5c6c2', borderRadius: 6, padding: '8px 12px', marginTop: 8 }}>
              {searchError}
            </div>
          )}
          {searched && results.length === 0 && !loading && !searchError && (
            <div style={{ fontSize: 13, color: '#7a99b8', marginTop: 8 }}>
              No results found. Try last name only, or check the out-of-state box.
            </div>
          )}
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 10 }}>
            <input
              type="checkbox"
              id="manualMode"
              checked={manualMode}
              onChange={e => setManualMode(e.target.checked)}
              style={{ width: 16, height: 16, cursor: 'pointer', accentColor: '#1a3364' }}
            />
            <label htmlFor="manualMode" style={{ fontSize: 13, color: '#4a6288', cursor: 'pointer' }}>
              Can't find your doctor? Enter their info manually
            </label>
          </div>
          {manualMode && (
            <div style={{
              background: '#fff', border: '1.5px solid #c5d5e8', borderRadius: 8,
              padding: 14, marginTop: 10,
            }}>
              <div style={{ marginBottom: 10 }}>
                <label style={lbl}>Doctor Name <span style={{ fontWeight: 400, textTransform: 'none', letterSpacing: 0 }}>(required)</span></label>
                <input
                  style={{ ...inp, background: '#fff' }}
                  value={manualName}
                  onChange={e => setManualName(e.target.value)}
                  placeholder="e.g. Dr. Jane Smith"
                  autoComplete="off"
                />
              </div>
              <div style={{ marginBottom: 10 }}>
                <label style={lbl}>Specialty <span style={{ fontWeight: 400, textTransform: 'none', letterSpacing: 0 }}>(optional)</span></label>
                <input
                  style={{ ...inp, background: '#fff' }}
                  value={manualSpecialty}
                  onChange={e => setManualSpecialty(e.target.value)}
                  placeholder="e.g. Cardiology"
                  autoComplete="off"
                />
              </div>
              <div style={{ ...row2, marginBottom: 12 }}>
                <div>
                  <label style={lbl}>City <span style={{ fontWeight: 400, textTransform: 'none', letterSpacing: 0 }}>(optional)</span></label>
                  <input
                    style={{ ...inp, background: '#fff' }}
                    value={manualCity}
                    onChange={e => setManualCity(e.target.value)}
                    autoComplete="off"
                  />
                </div>
                <div>
                  <label style={lbl}>State <span style={{ fontWeight: 400, textTransform: 'none', letterSpacing: 0 }}>(optional)</span></label>
                  <input
                    style={{ ...inp, background: '#fff' }}
                    value={manualState}
                    onChange={e => setManualState(e.target.value)}
                    placeholder="e.g. CO"
                    maxLength={2}
                    autoComplete="off"
                  />
                </div>
              </div>
              <div style={{ display: 'flex', gap: 8 }}>
                <button
                  type="button"
                  onClick={handleAddManual}
                  disabled={!manualName.trim()}
                  style={{
                    padding: '9px 22px', border: 'none', borderRadius: 8,
                    background: manualName.trim() ? '#2a5298' : '#b8cfe8',
                    color: '#fff', fontWeight: 600, fontSize: 14,
                    cursor: manualName.trim() ? 'pointer' : 'default',
                  }}
                >
                  + Add Doctor
                </button>
                <button
                  type="button"
                  onClick={() => {
                    setManualMode(false);
                    setManualName('');
                    setManualSpecialty('');
                    setManualCity('');
                    setManualState('');
                  }}
                  style={{
                    padding: '9px 16px', border: '1.5px solid #c5d5e8', borderRadius: 8,
                    background: '#fff', color: '#4a6288', fontWeight: 600, fontSize: 14, cursor: 'pointer',
                  }}
                >
                  Cancel
                </button>
              </div>
            </div>
          )}
          {results.length === 200 && (
            <div style={{ fontSize: 13, color: '#c0860a', background: '#fdf6e8', border: '1px solid #f0deb0', borderRadius: 6, padding: '8px 12px', marginTop: 8, marginBottom: 8 }}>
              Showing the first 200 results. Add a first name or check the out-of-state box differently to narrow your search.
            </div>
          )}
          {results.length > 0 && (
            <div style={{
              background: '#fff', border: '1.5px solid #c5d5e8', borderRadius: 8,
              maxHeight: 280, overflowY: 'auto',
              boxShadow: '0 4px 12px rgba(26,51,100,0.10)',
            }}>
              {results.map((doc, i) => (
                <div
                  key={i}
                  onClick={() => handleSelect(doc)}
                  style={{
                    padding: '10px 14px', cursor: 'pointer',
                    borderBottom: i < results.length - 1 ? '1px solid #f4f8fc' : 'none',
                  }}
                  onMouseEnter={e => e.currentTarget.style.background = '#dbeef8'}
                  onMouseLeave={e => e.currentTarget.style.background = '#fff'}
                >
                  <div style={{ fontWeight: 700, color: '#1a3364', fontSize: 14 }}>
                    {doc.name}{doc.credential ? `, ${doc.credential}` : ''}
                  </div>
                  <div style={{ fontSize: 12, color: '#4a6288', marginTop: 2 }}>
                    {[doc.specialty, doc.city, doc.state].filter(Boolean).join(' - ')}
                  </div>
                  <div style={{ fontSize: 11, color: '#7a99b8', marginTop: 1 }}>
                    NPI: {doc.npi}
                  </div>
                </div>
              ))}
            </div>
          )}
        </form>
      </div>

      {doctors.length > 0 && (
        <div>
          <div style={{
            fontSize: 13, fontWeight: 700, color: '#1a3364',
            letterSpacing: '0.05em', textTransform: 'uppercase',
            marginBottom: 10, display: 'flex', alignItems: 'center', gap: 8,
          }}>
            Added Doctors
            <span style={{
              background: '#1a3364', color: '#fff', borderRadius: '999px',
              padding: '1px 8px', fontSize: 12, fontWeight: 700,
              letterSpacing: 0, textTransform: 'none',
            }}>
              {doctors.length}
            </span>
          </div>
          {doctors.map((doc, i) => (
            <div key={i} style={{
              display: 'flex', alignItems: 'center', justifyContent: 'space-between',
              background: '#fff', border: '1.5px solid #dbeef8', borderRadius: 8,
              padding: '11px 14px', marginBottom: 8,
            }}>
              <div>
                <div style={{ fontWeight: 700, color: '#1a3364', fontSize: 14 }}>
                  {doc.name}{doc.credential ? `, ${doc.credential}` : ''}
                </div>
                <div style={{ fontSize: 13, color: '#4a6288', marginTop: 2 }}>
                  {[doc.specialty, doc.city, doc.state].filter(Boolean).join(' - ')}
                </div>
                {doc.npi && (
                  <div style={{ fontSize: 12, color: '#7a99b8', marginTop: 1 }}>
                    NPI: {doc.npi}
                  </div>
                )}
              </div>
              <button type="button" onClick={() => handleRemove(i)} style={{
                padding: '5px 10px', border: 'none', background: '#f5e8e8',
                borderRadius: 6, cursor: 'pointer', color: '#b03030',
                fontWeight: 700, fontSize: 14, flexShrink: 0, marginLeft: 12,
              }}>
                x
              </button>
            </div>
          ))}
        </div>
      )}

      {doctors.length === 0 && (
        <NoneCheckbox
          checked={data.noDoctors}
          onChange={v => onChange('noDoctors', v)}
          label="I don't have any doctors to add"
        />
      )}

      <PreferredProviders data={data} onChange={onChange} ignoreClicksUntilRef={ignoreClicksUntilRef} />
    </div>
  );
}
